import React, { createContext, useContext, useState, useEffect } from "react";
import { toast } from "sonner";
interface AuthState {
  isAuthenticated: boolean;
  hasBookedAppointment: boolean;
  loginCount: number;
  mfaEnabled: boolean;
  login: () => void;
  logout: () => void;
  markAppointmentBooked: () => void;
  changePassword: (oldPw: string, newPw: string) => Promise<boolean>;
  deleteAccount: () => void;
  toggleMfa: (enabled: boolean) => void;
  logoutOtherDevices: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasBookedAppointment, setHasBookedAppointment] = useState(false);
  const [loginCount, setLoginCount] = useState(0);
  const [mfaEnabled, setMfaEnabled] = useState(false);

  useEffect(() => {
    const auth = localStorage.getItem("srg_auth");
    const booked = localStorage.getItem("srg_booked");
    const count = localStorage.getItem("srg_login_count");
    const mfa = localStorage.getItem("srg_mfa");

    if (auth === "true") setIsAuthenticated(true);
    if (booked === "true") setHasBookedAppointment(true);
    if (count) setLoginCount(parseInt(count, 10));
    if (mfa === "true") setMfaEnabled(true);
  }, []);

  const login = () => {
    setIsAuthenticated(true);
    const newCount = loginCount + 1;
    setLoginCount(newCount);
    localStorage.setItem("srg_auth", "true");
    localStorage.setItem("srg_login_count", newCount.toString());
  };

  const logout = () => {
    setIsAuthenticated(false);
    setHasBookedAppointment(false);
    localStorage.removeItem("srg_auth");
    localStorage.removeItem("srg_booked");
    localStorage.removeItem("rollover_auth");
  };

  const markAppointmentBooked = () => {
    setHasBookedAppointment(true);
    localStorage.setItem("srg_booked", "true");
  };

  const changePassword = async (oldPw: string, newPw: string) => {
    // Mock API call
    return new Promise<boolean>((resolve) =>
      setTimeout(() => resolve(true), 800),
    );
  };

  const deleteAccount = () => {
    logout();
    localStorage.removeItem("srg_login_count");
    localStorage.removeItem("srg_mfa");
  };

  const toggleMfa = (enabled: boolean) => {
    setMfaEnabled(enabled);
    localStorage.setItem("srg_mfa", enabled ? "true" : "false");
    // In a real app, this would trigger an activity log
  };

  const logoutOtherDevices = () => {
    // Mock implementation
    toast.success("Successfully logged out of all other devices.");
  };

  // Auto-logout after 15 minutes of inactivity
  useEffect(() => {
    if (!isAuthenticated) return;

    let timeoutId: NodeJS.Timeout;

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(
        () => {
          logout();
          toast.info("You have been logged out due to inactivity.", {
            duration: 5000,
          });
          window.dispatchEvent(new CustomEvent("auth-timeout"));
        },
        15 * 60 * 1000,
      ); // 15 minutes
    };

    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((event) => document.addEventListener(event, resetTimer));
    resetTimer();

    return () => {
      clearTimeout(timeoutId);
      events.forEach((event) =>
        document.removeEventListener(event, resetTimer),
      );
    };
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        hasBookedAppointment,
        loginCount,
        mfaEnabled,
        login,
        logout,
        markAppointmentBooked,
        changePassword,
        deleteAccount,
        toggleMfa,
        logoutOtherDevices,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
